import React from 'react';
import { fund, fundSections } from '../../models/content';

export default function V2FundPage() {
  return (
    <main>
      <section className="page-hero">
        <div className="wrap">
          <span className="v2-badge">
            <span className="dot" />
            {fund.heroBadge}
          </span>
          <h1>
            {fund.heroTitle.map((p, i) =>
              typeof p === 'string' ? (
                <React.Fragment key={i}>{p}</React.Fragment>
              ) : (
                <span key={i} className="it">
                  {p.it}
                </span>
              )
            )}
          </h1>
          <p className="sub">{fund.heroSub}</p>
        </div>
      </section>

      <section>
        <div className="wrap">
          <div className="hybrid">
            <span className="kicker">Hybrid Structure</span>
            <h2 style={{ font: '700 44px/1.15 var(--f-sans)', letterSpacing: '-0.02em', margin: '14px 0 8px', color: 'var(--p950)' }}>
              <span className="it" style={{ fontStyle: 'italic', color: 'var(--p700)' }}>
                Downside protection
              </span>{' '}
              + Upside participation.
            </h2>

            <div className="duo">
              <div className="blob">Debt</div>
              <div className="plus">+</div>
              <div className="blob alt">Equity Options / Equity</div>
            </div>
            <div className="labels">
              <span>Downside Protection</span>
              <span>Upside Participation</span>
            </div>

            <div className="timeline">
              <div className="node" style={{ left: -7 }} />
              <div className="pill">Invest</div>
              <div className="node" style={{ left: '33.33%' }} />
              <div className="pill">Growth &amp; Turnaround</div>
              <div className="node" style={{ left: '66.66%' }} />
              <div className="pill">Exit</div>
              <div className="node" style={{ right: -7 }} />
            </div>

            <div className="three-col">
              <div className="col">
                <div className="vlabel">Solve for</div>
                <div className="items">
                  <div>Capital Structure</div>
                  <div>Rationalize Operations</div>
                  <div>Corporate Governance</div>
                </div>
              </div>
              <div className="col">
                <div className="vlabel">Harvest Cash</div>
                <div className="items">
                  <div>Sustainable Debt at Current Pay</div>
                  <div>Recover Majority of Invested Capital</div>
                  <div>Value Creation</div>
                </div>
              </div>
              <div className="col">
                <div className="vlabel">Exit</div>
                <div className="items">
                  <div>Refinance of Debt</div>
                  <div>IPO</div>
                  <div>Strategic Sale</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id={fundSections.operate} className="strategy-v2 fund-section-anchor">
        <div className="wrap">
          <div className="head">
            <span className="kicker">§ 01</span>
            <h2>
              How Do We <span className="it">Operate?</span>
            </h2>
          </div>
          <div className="pillars op-grid">
            <article className="pillar">
              <span className="bg-num">01</span>
              <span className="eyebrow">Approach</span>
              <p>
                Designed to be significant equity-oriented, we believe in value investing in scalable businesses facing capital issues with
                demonstrated past track records.
              </p>
            </article>
            <article className="pillar">
              <span className="bg-num">02</span>
              <span className="eyebrow">Partnership</span>
              <p>
                We seek to work with promoters who have demonstrated unwavering commitment and are receptive to implementing a focused
                resurgence strategy. We recognise that every business and transaction is unique in nature.
              </p>
            </article>
            <article className="pillar">
              <span className="bg-num">03</span>
              <span className="eyebrow">Structure</span>
              <p>
                Our investments are specifically engineered to provide a holistic solution to each of our portfolio companies, and are
                predominantly structured through a combination of debt and significant equity.
              </p>
            </article>
            <article className="pillar">
              <span className="bg-num">04</span>
              <span className="eyebrow">Protection</span>
              <p>
                The debt structure provides downside risk protection and an avenue to seek progressive returns on investments from cash flows.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section id={fundSections.contribute} className="fund-section-anchor">
        <div className="wrap">
          <div style={{ textAlign: 'center', maxWidth: 920, margin: '0 auto' }}>
            <span className="kicker">§ 02</span>
            <h2 style={{ font: '700 44px/1.15 var(--f-sans)', letterSpacing: '-0.02em', margin: '16px 0 0', color: 'var(--p950)' }}>
              How Do We <span className="it" style={{ fontStyle: 'italic', color: 'var(--p700)' }}>Contribute?</span>
            </h2>
            <p style={{ margin: '20px auto 0', maxWidth: 760, color: 'var(--g800)', fontSize: 18 }}>
              We develop a fundamentally strong working relationship with promoters and management to ensure that a strong resurgence strategy
              is effectively implemented.
            </p>
            <p style={{ margin: '8px auto 0', color: 'var(--g700)' }}>
              We work actively with the management of investee entities in:
            </p>
          </div>

          <div className="steps">
            <div className="step">
              <div className="num">01</div>
              <div className="body">Formulating a comprehensive turnaround plan.</div>
            </div>
            <div className="step">
              <div className="num">02</div>
              <div className="body">Identifying ways to improve the key financial levers of a business.</div>
            </div>
            <div className="step">
              <div className="num">03</div>
              <div className="body">Upgrading existing internal controls, systems, and processes.</div>
            </div>
            <div className="step">
              <div className="num">04</div>
              <div className="body">Strengthening corporate governance and improving management.</div>
            </div>
          </div>
        </div>
      </section>

      <section id={fundSections.addValue} className="fund-section-anchor">
        <div className="add-value-block">
          <span className="kicker">§ 03</span>
          <h2>
            How Do We <span style={{ fontStyle: 'italic', color: 'var(--p300)' }}>Add Value</span> To Our Investors?
          </h2>
          <p>
            Our primary aim is to provide our portfolio companies with a sustainable capital solution — one that enables them to scale their
            operations with minimal additional support. We seek to accomplish this objective by engineering an optimised investment structure
            that specifically caters to each portfolio company’s individual requirements.
          </p>
          <p>
            We seek to recover invested capital through the company’s cash flows, while actively creating an exit for our equity holding in
            the company.
          </p>
        </div>
      </section>
    </main>
  );
}

